import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Money } from "./SaudiRiyal";
import { api } from "../lib/api";

const MONTHS = [3, 6, 9, 12];
const today = () => new Date(Date.now() + 3 * 3600000).toISOString().slice(0, 10);
const addMonths = (iso, n) => {
  const d = iso ? new Date(iso + "T00:00:00Z") : new Date(today() + "T00:00:00Z");
  const day = d.getUTCDate();
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1));
  const last = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0)).getUTCDate();
  t.setUTCDate(Math.min(day, last));
  return t.toISOString().slice(0, 10);
};

export const RenewIqamaDialog = ({ open, onOpenChange, employees, onDone }) => {
  const [fees, setFees] = useState(null);
  const [months, setMonths] = useState(3);
  const [paid, setPaid] = useState("");
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setMonths(3); setPaid(""); setDate(today()); setNote(""); api.renewalForecast().then(setFees).catch(() => setFees({ iqama_fee: 650, permit_fee: 9700 })); } }, [open]);
  const per = fees ? Math.round(((fees.iqama_fee + fees.permit_fee) * months) / 12 * 100) / 100 : 0;
  const calc = Math.round(per * employees.length * 100) / 100;
  const paidNum = paid === "" ? calc : Number(paid);
  const diff = Math.round((paidNum - calc) * 100) / 100;
  const save = async () => {
    setBusy(true);
    try {
      const doc = await api.renewIqamas({ employee_ids: employees.map((e) => e.id), months, paid_amount: paidNum, paid_date: date, note });
      toast.success(`تم تسجيل تجديد ${employees.length} إقامة لمدة ${months === 12 ? "سنة" : `${months} أشهر`}`);
      onDone?.(doc); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التسجيل"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="renew-iqama-dialog">
        <DialogHeader className="text-start"><DialogTitle className="flex items-center gap-2"><RefreshCcw className="h-5 w-5 text-emerald-600" /> تسجيل تجديد الإقامة وكرت العمل</DialogTitle>
          <DialogDescription>يتمدّد تاريخ انتهاء الإقامة تلقائياً ويُسجّل المبلغ المدفوع للمقارنة.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>مدة التجديد</Label>
            <div className="flex gap-1.5" data-testid="renew-months">{MONTHS.map((m) => (
              <button key={m} type="button" onClick={() => setMonths(m)} data-testid={`renew-months-${m}`}
                className={`flex-1 rounded-xl border py-2 text-sm font-bold transition-colors ${months === m ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-emerald-300"}`}>{m === 12 ? "سنة" : `${m} أشهر`}</button>))}</div>
          </div>
          <div className="max-h-48 divide-y divide-slate-100 overflow-y-auto rounded-xl border border-slate-200 text-sm" data-testid="renew-list">
            {employees.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-2 px-3 py-2">
                <span className="min-w-0 truncate font-semibold text-slate-800">{e.name}</span>
                <span dir="ltr" className="shrink-0 font-mono-nums text-xs text-slate-500">{e.expiry || "—"} → <b className="text-emerald-700">{addMonths(e.expiry, months)}</b></span>
              </div>))}
          </div>
          <div className="space-y-1 rounded-xl bg-slate-50 p-3 text-xs" data-testid="renew-calc">
            <div className="flex justify-between"><span>لكل مقيم: ({fees?.permit_fee ?? "…"} + {fees?.iqama_fee ?? "…"}) × {months} ÷ 12</span><Money value={per} /></div>
            <div className="flex justify-between font-bold text-slate-800"><span>المحسوب ({employees.length} مقيم)</span><span data-testid="renew-calculated"><Money value={calc} /></span></div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>المدفوع فعلياً</Label><Input type="number" value={paid} placeholder={String(calc)} onChange={(e) => setPaid(e.target.value)} data-testid="renew-paid" /></div>
            <div className="space-y-1.5"><Label>تاريخ الدفع</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="renew-date" /></div>
          </div>
          {diff !== 0 && <p className={`rounded-lg px-3 py-2 text-xs font-semibold ${diff > 0 ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`} data-testid="renew-diff">
            {diff > 0 ? "مدفوع أكثر من المحسوب بـ" : "مدفوع أقل من المحسوب بـ"} <Money value={Math.abs(diff)} /></p>}
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="ملاحظة (اختياري) — مثل: رقم سداد" data-testid="renew-note" />
          <Button className="w-full gap-1.5 bg-emerald-600 hover:bg-emerald-700" disabled={busy || !employees.length || !fees || !(paidNum >= 0)} onClick={save} data-testid="renew-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} تم التجديد ({employees.length})</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
